import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft, Loader2, Check, ShoppingBag } from "lucide-react";
import { api, GHS } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => { setProduct(r.data); setActiveImg(r.data?.image || null); }).catch(() => setProduct(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;
  if (!product) return <div className="py-32 text-center text-brand-muted">Product not found.</div>;

  const out = product.stock <= 0;
  const tiktokId = (product.tiktok_url || "").match(/\/video\/(\d+)/)?.[1];
  const add = () => { addItem(product, qty); toast.success(`${qty} × ${product.name} added to cart`); };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: product.image,
    description: product.description,
    category: product.category,
    brand: { "@type": "Brand", name: "Yayra Glover Organic Inputs" },
    offers: {
      "@type": "Offer",
      priceCurrency: "GHS",
      price: product.price,
      availability: out ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
    },
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <Seo title={product.name} path={`/product/${product.product_id}`} description={product.description} image={product.image} jsonLd={jsonLd} />
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-brand-muted hover:text-brand text-sm mb-8"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="grid gap-10 md:grid-cols-2">
        <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-sm overflow-hidden bg-white border border-brand/10 aspect-square flex items-center justify-center">
            <img src={activeImg || product.image} alt={product.name} className="w-full h-full object-contain p-2" data-testid="product-image" />
          </motion.div>

          {product.gallery?.length > 0 && (
            <div className="mt-3 flex gap-3 flex-wrap" data-testid="product-gallery">
              {[product.image, ...product.gallery].map((src, i) => (
                <button
                  key={src}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActiveImg(src)}
                  className={`w-16 h-16 rounded-sm overflow-hidden bg-white border-2 transition-colors flex items-center justify-center ${(activeImg || product.image) === src ? "border-brand" : "border-brand/10 hover:border-brand/40"}`}
                >
                  <img src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}

          {product.video && (
            <div className="mt-6" data-testid="product-video">
              <h3 className="text-xs uppercase tracking-[0.18em] text-brand font-semibold mb-2">Watch: How it's made</h3>
              <video
                src={product.video}
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-sm bg-black aspect-video"
              />
            </div>
          )}

          {tiktokId && (
            <div className="mt-6" data-testid="product-tiktok">
              <h3 className="text-xs uppercase tracking-[0.18em] text-brand font-semibold mb-2">See it on TikTok</h3>
              <div className="rounded-sm overflow-hidden bg-black mx-auto" style={{ maxWidth: 325 }}>
                <iframe
                  title="TikTok video"
                  src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
                  className="w-full"
                  style={{ height: 575, border: "none" }}
                  allow="autoplay; encrypted-media; fullscreen"
                  scrolling="no"
                />
              </div>
              <a href={product.tiktok_url} target="_blank" rel="noreferrer noopener"
                className="mt-2 inline-block text-sm text-brand-terracotta underline underline-offset-2 hover:text-brand-hover"
                data-testid="tiktok-watch-link">
                Watch on TikTok →
              </a>
            </div>
          )}
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">{product.category}</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-brand leading-tight" data-testid="product-name">{product.name}</h1>
          <p className="mt-4 text-2xl font-bold text-brand" data-testid="product-price">{GHS(product.price)}</p>
          <p className="text-sm text-brand-muted mt-1">Per {product.unit}</p>
          <p className="mt-6 text-brand-muted leading-relaxed" data-testid="product-description">{product.description}</p>

          {(product.composition || product.application || product.crops || product.certifications) && (
            <div className="mt-7 space-y-4 border-t border-brand/10 pt-6" data-testid="product-details">
              {product.composition && (
                <div>
                  <h3 className="text-xs uppercase tracking-[0.18em] text-brand font-semibold">Composition</h3>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">{product.composition}</p>
                </div>
              )}
              {product.application && (
                <div>
                  <h3 className="text-xs uppercase tracking-[0.18em] text-brand font-semibold">Application & Dosage</h3>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">{product.application}</p>
                </div>
              )}
              {product.crops && (
                <div>
                  <h3 className="text-xs uppercase tracking-[0.18em] text-brand font-semibold">Suitable Crops</h3>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">{product.crops}</p>
                </div>
              )}
              {product.certifications && (
                <div>
                  <h3 className="text-xs uppercase tracking-[0.18em] text-brand font-semibold">Certifications</h3>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">{product.certifications}</p>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 text-sm">
            {out ? <span className="text-brand-terracotta font-medium">Out of stock</span>
                 : <span className="text-brand flex items-center gap-1.5"><Check className="w-4 h-4" /> In stock ({product.stock} available)</span>}
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-brand/20 rounded-full">
              <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-brand"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-medium" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="p-3 text-brand"><Plus className="w-4 h-4" /></button>
            </div>
            <Button data-testid="detail-add-to-cart" onClick={add} disabled={out} className="flex-1 bg-brand hover:bg-brand-hover text-white rounded-full h-13 gap-2 text-base">
              <ShoppingBag className="w-5 h-5" /> Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
